import { useState } from "react"
import { useDispatch } from "react-redux"
import { Eye, EyeOff } from "react-feather"
import { POST_M_User_InsertAPI } from "../../helper/Redux/Slices/UserCreationSlice/UserCreationSlice"
import "./AcademyRegistrationForm.css"

const initialForm = {
    firstName: "",
    lastName: "",
    email: "",
    contactNumber: "",
    UserID: "",
    password: "",
    confirmPassword: "",
}

const validateForm = (form) => {
    const errors = {}
    const namePattern = /^[A-Za-z]+(?:[ '-][A-Za-z]+)*$/
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/
    const phoneDigits = form.contactNumber.replace(/\D/g, "")

    if (!namePattern.test(form.firstName.trim())) {
        errors.firstName = "Enter a valid first name."
    }
    if (!namePattern.test(form.lastName.trim())) {
        errors.lastName = "Enter a valid last name."
    }
    if (!emailPattern.test(form.email.trim())) {
        errors.email = "Enter a valid email address."
    }
    if (!/^\+?[0-9 ]+$/.test(form.contactNumber.trim()) || phoneDigits.length < 10 || phoneDigits.length > 13) {
        errors.contactNumber = "Enter 10 to 13 digits. You may include spaces and a leading +."
    }
    if (!form.UserID.trim()) {
        errors.UserID = "Enter a user name."
    }
    if (!form.password) {
        errors.password = "Create a password."
    }
    if (!form.confirmPassword) {
        errors.confirmPassword = "Confirm your password."
    } else if (form.password !== form.confirmPassword) {
        errors.confirmPassword = "Passwords do not match."
    }

    return errors
}

export const AcademyRegistrationForm = ({ onBackToLogin }) => {
    const dispatch = useDispatch()
    const [errors, setErrors] = useState({})
    const [touched, setTouched] = useState({})
    const [submitted, setSubmitted] = useState(false)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [message, setMessage] = useState("")
    const [visiblePasswords, setVisiblePasswords] = useState({
        password: false,
        confirmPassword: false,
    })

    const [formData, setFormData] = useState(initialForm)

    const handleChange = (event) => {
        const { name, value } = event.target
        const updatedFormData = { ...formData, [name]: value }
        setFormData(updatedFormData)
        setTouched((currentTouched) => ({ ...currentTouched, [name]: true }))
        setErrors(validateForm(updatedFormData))
        setMessage("")
    }

    const handleBlur = (event) => {
        const { name } = event.target
        setTouched((currentTouched) => ({ ...currentTouched, [name]: true }))
        setErrors(validateForm(formData))
    }

    const handleIsPost = (responseMessage) => {
        setFormData({ ...initialForm })
        setErrors({})
        setTouched({})
        setSubmitted(false)
        setVisiblePasswords({ password: false, confirmPassword: false })
        setMessage(responseMessage || "Registration successful. You can sign in now.")
    }

    const handleSubmit = async (event) => {
        event.preventDefault()
        const validationErrors = validateForm(formData)
        setSubmitted(true)
        setErrors(validationErrors)

        if (Object.keys(validationErrors).length > 0) {
            setMessage("")
            return
        }

        setMessage("")
        const data = {
            UserName: formData.UserID.trim(),
            Password: formData.password,
            FirstName: formData.firstName.trim(),
            LastName: formData.lastName.trim(),
            EmailID: formData.email.trim(),
            ContactNumber: formData.contactNumber.trim(),
            ScreenType: "Insert",
            setloader: setIsSubmitting,
            handleIsPost: handleIsPost,
        }

        dispatch(POST_M_User_InsertAPI({ data }))
    }

    const field = (name, label, type, autoComplete, options = {}) => {
        const showError = Boolean(errors[name] && (touched[name] || submitted))

        return (
            <div className="mb-3">
                <label htmlFor={`academy-register-${name}`} className="form-label fw-semibold">
                    {label}
                </label>
                <div className={type === "password" ? "input-group" : undefined}>
                    <input
                        id={`academy-register-${name}`}
                        name={name}
                        type={type === "password" && visiblePasswords[name] ? "text" : type}
                        className={`form-control${showError ? " is-invalid" : ""}`}
                        autoComplete={autoComplete}
                        value={formData[name]}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        aria-invalid={showError}
                        aria-describedby={showError ? `academy-register-${name}-error` : undefined}
                        {...options}
                    />
                    {type === "password" && (
                        <button
                            type="button"
                            className="btn btn-outline-secondary"
                            onClick={() => setVisiblePasswords((current) => ({
                                ...current,
                                [name]: !current[name],
                            }))}
                            aria-label={visiblePasswords[name] ? `Hide ${label.toLowerCase()}` : `Show ${label.toLowerCase()}`}
                            aria-pressed={visiblePasswords[name]}
                        >
                            {visiblePasswords[name] ? <EyeOff size={18} /> : <Eye size={18} />}
                        </button>
                    )}
                </div>
                {showError && (
                    <div id={`academy-register-${name}-error`} className="invalid-feedback d-block" role="alert">
                        {errors[name]}
                    </div>
                )}
            </div>
        )
    }

    return (
        <div aria-labelledby="academy-registration-heading">
            <h2 id="academy-registration-heading" className="fw-bold mb-2" style={{ color: "#16374e" }}>
                Create your account
            </h2>
            <p className="text-muted mb-4">Enter your details to register for MILSO Academy.</p>

            <form onSubmit={handleSubmit} noValidate>
                <div className="row">
                    <div className="col-12 col-md-6">
                        {field("firstName", "First Name", "text", "given-name")}
                    </div>
                    <div className="col-12 col-md-6">
                        {field("lastName", "Last Name", "text", "family-name")}
                    </div>
                </div>
                {field("email", "Email ID", "email", "email")}
                {field("contactNumber", "Contact Number", "tel", "tel", { inputMode: "tel" })}
                {field("UserID", "User Name", "text", "user-id")}
                {field("password", "Create Password", "password", "new-password")}
                {field("confirmPassword", "Confirm Password", "password", "new-password")}

                {message && (
                    <p className="academy-registration-success" role="status">
                        {message}
                    </p>
                )}

                <button
                    type="submit"
                    className="btn fw-semibold w-100 py-2"
                    style={{ backgroundColor: "#16374e", color: "#fff" }}
                    disabled={isSubmitting}
                >
                    {isSubmitting ? "Registering..." : "Register"}
                </button>
            </form>

            <p className="text-center mt-3 mb-0">
                Already registered?{" "}
                <button type="button" className="academy-registration-link" onClick={onBackToLogin}>
                    Sign in
                </button>
            </p>
        </div>
    )
}
